import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { CurrencySymbols } from '../../../constants/enums';

import type {
  AccountStatusData,
  BalanceTotal,
  CurrentVsAverage,
  CurrentVsPrevious,
  Widget,
  WidgetColors,
  WidgetData,
  WidgetName,
  WidgetMode,
  WidgetProps,
  Widgets,
} from './types';

import CustomTooltip from './CustomTooltip/CustomToolTip';
import CustomAxisTick from './CustomAxisTick/CustomAxisTick';

import { formatAsCurrency } from '../../../utils/utils';

const colors: WidgetColors = {
  active: {
    stroke: '#222',
    fill: '#28BE9A',
  },
  inactive: {
    stroke: '#222',
    fill: '#EDEDED',
  },
  primary: {
    stroke: '#222',
    fill: '#9251A1',
  },
  primaryLight: {
    stroke: '#222',
    fill: '#fff',
  },
  primaryDark: {
    stroke: '#222',
    fill: '#222',
  },
  mediumDark: {
    stroke: '#222',
    fill: '#EDEDED',
  },
};

const currencySymbols: { [currency: string]: CurrencySymbols } = {
  CAD: CurrencySymbols.CAD,
  USD: CurrencySymbols.USD,
  GBP: CurrencySymbols.GBP,
  EUR: CurrencySymbols.EUR,
};

const widgets: Widgets = {
  accountStatus: {
    title: () => 'Account Status',
    defaultData: [
      { label: 'Active', total: 0, active: 0 },
      { label: 'InActive', total: 0, inactive: 0 },
    ],
    getVerifiedData: (data) => {
      const { defaultData } = widgets.accountStatus;

      return isDataArray(data) && Object.keys(data).length !== 0 ? data : defaultData;
    },
    hero: ({ data: _data, showDetails }: WidgetProps) => {
      const { getVerifiedData } = widgets.accountStatus;

      const data = getVerifiedData(_data) as AccountStatusData[];

      return (
        <ComposedChart data={data} margin={{ top: 20 }}>
          {showDetails && <Legend align="right" verticalAlign="top" formatter={legendFormatter} />}
          {showDetails && <CartesianGrid strokeDasharray="3 3" />}
          {showDetails && (
            <Tooltip
              isAnimationActive={false}
              content={
                <CustomTooltip labels={{ total: 'Number of Accounts', active: 'Active', inactive: 'Inactive' }} />
              }
            />
          )}

          <Area dataKey="total" stroke={colors.primary.stroke} fill={colors.primary.fill} />
          <Bar dataKey="active" stackId="1" stroke={colors.active.stroke} fill={colors.active.fill} />
          <Bar dataKey="inactive" stackId="1" stroke={colors.inactive.stroke} fill={colors.inactive.fill} />

          <YAxis allowDecimals={false} width={50} />
          <XAxis dataKey="label" tick={<CustomAxisTick />} height={50} />
        </ComposedChart>
      );
    },
    preview: ({ data: _data }: WidgetProps) => {
      const { getVerifiedData } = widgets.accountStatus;

      const data = getVerifiedData(_data) as AccountStatusData[];

      return (
        <ComposedChart data={data} margin={{ top: 20 }}>
          <Area dataKey="total" stroke={colors.primary.stroke} fill={colors.primary.fill} />
          <Bar dataKey="active" stackId="1" stroke={colors.active.stroke} fill={colors.active.fill} />
          <Bar dataKey="inactive" stackId="1" stroke={colors.inactive.stroke} fill={colors.inactive.fill} />

          <XAxis tick={false} />
        </ComposedChart>
      );
    },
  },

  balanceTotals: {
    title: ({ data, currentPanel }) => {
      const currency = Object.keys(data)[currentPanel - 1];

      return `Balance Totals ${currency ? `| ${currency}` : ''}`;
    },
    defaultData: { Currency: [{ label: 'Transaction Type', total: 0 }] },
    getVerifiedData: (data) => {
      const { defaultData } = widgets.balanceTotals;

      return isKeyValueArray(data) && Object.keys(data).length !== 0 ? data : defaultData;
    },
    hero: ({ data: _data, currentPanel, showDetails }: WidgetProps) => {
      const { getVerifiedData } = widgets.dailyTransactionBreakdown;

      const data = getVerifiedData(_data) as { [date: string]: BalanceTotal[] };
      const panelData = Object.values(data)[currentPanel - 1];

      const currencyAbbr = Object.keys(data)[currentPanel - 1];
      const currencySymbol = currencySymbols[currencyAbbr] || '';
      const tickUnit = calculateTickUnit(panelData);

      return (
        <BarChart data={panelData} margin={{ top: 20 }}>
          {showDetails && <Legend align="right" verticalAlign="top" formatter={legendFormatter} />}
          {showDetails && <CartesianGrid strokeDasharray="3 3" />}
          {showDetails && (
            <Tooltip
              isAnimationActive={false}
              content={
                <CustomTooltip labels={{ total: `Balance Total - ${currencyAbbr}` }} formatter={currencyFormatter} />
              }
            />
          )}

          <Bar dataKey="total" stroke={colors.mediumDark.stroke} fill={colors.mediumDark.fill} />

          <YAxis tickFormatter={tickFormatter} name={currencySymbol} unit={tickUnit || currencySymbol} width={50} />
          <XAxis dataKey="label" tick={<CustomAxisTick />} height={50} />
        </BarChart>
      );

      function currencyFormatter(value: string): string {
        return formatAsCurrency(value, currencySymbol);
      }

      function calculateTickUnit(totals: BalanceTotal[]): string {
        const largestTotal = findLargestAbsTotal(totals);

        if (largestTotal > 1000000000) return 'B';
        if (largestTotal > 1000000) return 'M';
        if (largestTotal > 1000) return 'K';

        return '';
      }

      function findLargestAbsTotal(totals: BalanceTotal[]): number {
        const init = 0;

        return totals.reduce(
          (accumulator, { total }) => (accumulator ? Math.max(accumulator, Math.abs(total)) : Math.abs(total)),
          init,
        );
      }

      function tickFormatter(value: number) {
        const handlers: { [key: string]: () => number } = {
          B: () => value / 1000000000,
          M: () => value / 1000000,
          K: () => value / 1000,
          default: () => value,
        };

        const getTickValue = handlers[tickUnit] || handlers.default;

        return `${getTickValue()}`;
      }
    },
    preview: ({ data: _data, currentPanel }: WidgetProps) => {
      const { getVerifiedData } = widgets.dailyTransactionBreakdown;

      const data = getVerifiedData(_data) as { [date: string]: CurrentVsAverage[] };
      const panelData = Object.values(data)[currentPanel - 1];

      return (
        <BarChart data={panelData} margin={{ top: 20 }}>
          <Bar dataKey="total" stroke={colors.mediumDark.stroke} fill={colors.mediumDark.fill} />

          <XAxis tick={false} />
        </BarChart>
      );
    },
  },

  dailyTransactionBreakdown: {
    title: ({ data, currentPanel }) => {
      const panelDate = Object.keys(data)[currentPanel - 1];

      return `Daily Transaction Breakdown ${panelDate ? `| ${panelDate}` : ''}`;
    },
    defaultData: { Date: [{ label: 'Transaction Type', current: 0, average: 0 }] },
    getVerifiedData: (data) => {
      const { defaultData } = widgets.dailyTransactionBreakdown;

      return isKeyValueArray(data) && Object.keys(data).length !== 0 ? data : defaultData;
    },
    hero: ({ data: _data, currentPanel, showDetails }: WidgetProps) => {
      const { getVerifiedData } = widgets.dailyTransactionBreakdown;

      const data = getVerifiedData(_data) as { [date: string]: CurrentVsAverage[] };
      const panelData = Object.values(data)[currentPanel - 1];
      const panelDate = Object.keys(data)[currentPanel - 1];

      return (
        <ComposedChart data={panelData} margin={{ top: 20 }}>
          {showDetails && <Legend align="right" verticalAlign="top" formatter={legendFormatter} />}
          {showDetails && <CartesianGrid strokeDasharray="3 3" />}
          {showDetails && (
            <Tooltip
              isAnimationActive={false}
              content={<CustomTooltip labels={{ average: 'Average', current: `${panelDate}` }} />}
            />
          )}

          <Area dataKey="average" stroke={colors.primary.stroke} fill={colors.primary.fill} />
          <Bar dataKey="current" stroke={colors.mediumDark.stroke} fill={colors.mediumDark.fill} />

          <YAxis width={50} />
          <XAxis dataKey="label" tick={<CustomAxisTick />} height={50} />
        </ComposedChart>
      );
    },
    preview: ({ data: _data, currentPanel }: WidgetProps) => {
      const { getVerifiedData } = widgets.dailyTransactionBreakdown;

      const data = getVerifiedData(_data) as { [date: string]: CurrentVsAverage[] };
      const panelData = Object.values(data)[currentPanel - 1];

      return (
        <ComposedChart data={panelData} margin={{ top: 20 }}>
          <Area dataKey="average" stroke={colors.primary.stroke} fill={colors.primary.fill} />
          <Bar dataKey="current" stroke={colors.mediumDark.stroke} fill={colors.mediumDark.fill} />
          <XAxis tick={false} />
        </ComposedChart>
      );
    },
  },

  weeklyTransactionVolume: {
    title: () => 'Weekly Transaction Volume',
    defaultData: [{ label: 'Date', current: 0, previous: 0 }],
    getVerifiedData: (data) => {
      const { defaultData } = widgets.weeklyTransactionVolume;

      return isDataArray(data) && Object.keys(data).length !== 0 ? data : defaultData;
    },
    hero: ({ data: _data, showDetails }: WidgetProps) => {
      const { getVerifiedData } = widgets.weeklyTransactionVolume;
      const data = getVerifiedData(_data) as CurrentVsPrevious[];

      return (
        <LineChart data={data} margin={{ top: 20 }}>
          {showDetails && <Legend align="right" verticalAlign="top" formatter={legendFormatter} />}
          {showDetails && <CartesianGrid strokeDasharray="3 3" />}
          {showDetails && (
            <Tooltip
              isAnimationActive={false}
              content={<CustomTooltip labels={{ current: 'Current Volume', previous: 'Previous Volume' }} />}
            />
          )}

          <Line type="monotone" dataKey="current" stroke={colors.primaryDark.stroke} dot />
          <Line type="monotone" dataKey="previous" stroke={colors.mediumDark.stroke} dot strokeDasharray="5 5" />

          <YAxis width={50} />
          <XAxis dataKey="label" tick={<CustomAxisTick />} height={50} />
        </LineChart>
      );
    },
    preview: ({ data: _data }: WidgetProps) => {
      const { getVerifiedData } = widgets.weeklyTransactionVolume;

      const data = getVerifiedData(_data) as CurrentVsPrevious[];

      return (
        <LineChart data={data} margin={{ top: 20 }}>
          <Line type="monotone" dataKey="current" stroke={colors.primaryDark.stroke} dot={false} />
          <XAxis tick={false} />
        </LineChart>
      );
    },
  },
};

export const getWidget = (name: WidgetName, mode: WidgetMode, props: WidgetProps): Widget => {
  const widget = widgets[name];

  return {
    title: widget.title(props),
    panelCount: getPanelCount(props.data),
    widget: (
      <ResponsiveContainer width="100%" height={props.height}>
        {widget[mode](props)}
      </ResponsiveContainer>
    ),
  };
};

function legendFormatter(value: string) {
  return <span style={{ color: '#222' }}>{value}</span>;
}

function getPanelCount(data: WidgetData) {
  return isKeyValueArray(data) ? Object.keys(data).length : 1;
}

function isKeyValueArray(data: unknown[] | { [key: string]: unknown[] }): data is { [key: string]: unknown[] } {
  if (data.constructor === Object) {
    return Object.values(data).every((item) => Array.isArray(item));
  }

  return false;
}

function isDataArray(data: unknown[] | { [key: string]: unknown[] }): data is unknown[] {
  return Array.isArray(data);
}
